import { Navigate } from 'react-router-dom';
import { useUser } from '../hooks/useUser';
import { Box, CircularProgress, Typography } from '@mui/material';

/**
 * ロールベースのアクセス制御を行うコンポーネント
 * @param {Object} props - コンポーネントのプロパティ
 * @param {React.ReactNode} props.children - 保護されたコンポーネント
 * @param {string|string[]} props.allowedRoles - アクセスを許可するロール（配列または単一の文字列）
 * @param {string} props.redirectTo - アクセス拒否時のリダイレクト先（デフォルト: '/login'）
 * @returns {React.ReactNode} 保護されたコンポーネントまたはリダイレクト
 */
const ProtectedRoute = ({ children, allowedRoles, redirectTo = '/login' }) => {
  const { user, isLoading, error } = useUser();

  // ローディング中の表示
  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          認証情報を確認中...
        </Typography>
      </Box>
    );
  }

  // エラーまたはユーザー情報が取得できない場合
  if (error || !user) {
    return <Navigate to={redirectTo} replace />;
  }

  // ロールの正規化（配列に変換）
  const rolesArray = Array.isArray(allowedRoles)
    ? allowedRoles
    : [allowedRoles];

  // ユーザーのロールが許可されたロールに含まれているかチェック
  const hasAccess = rolesArray.includes(user.role);

  if (!hasAccess) {
    // アクセス権限がない場合はログイン画面にリダイレクト
    localStorage.clear();
    return <Navigate to={redirectTo} replace />;
  }

  // アクセス権限がある場合は子コンポーネントを表示
  return children;
};

export default ProtectedRoute;
